// ========================================
// pages/VerifyEmail.js - Email Verification Page
// ========================================
// PURPOSE: Handle email verification link clicks
// - Extracts token from URL
// - Sends to backend for verification
// - On success: Shows success message + auto-redirect (3 sec) + manual button
// - On failure: Shows error with option to resend
// ========================================

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from '../utils/axiosConfig';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const [status, setStatus] = useState('verifying');
  const [errorMessage, setErrorMessage] = useState('');
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(3);

  // ========================================
  // VERIFY EMAIL ON PAGE LOAD
  // ========================================
  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setStatus('error');
      setErrorMessage('No verification token provided');
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
		 console.log('API Base URL:', process.env.REACT_APP_API_URL);  // <-- ADD THIS LINE
		
      const response = await axios.post(`/api/auth/verify-email/${token}`);
      
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      setStatus('success');
      
      // Start countdown for auto-redirect
      let count = 3;
      const interval = setInterval(() => {
        count -= 1;
        setCountdown(count);
        if (count === 0) {
          clearInterval(interval);
          navigate('/dashboard');
        }
      }, 1000);
      
    } catch (err) {
      setStatus('error');
      setErrorMessage(err.response?.data?.error || 'Failed to verify email. The link may have expired.');
    }
  };

  // ========================================
  // RESEND VERIFICATION EMAIL
  // ========================================
  const resendVerification = async () => {
    setResending(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setErrorMessage('Please login first to request a new verification email');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }
      
      await axios.post('/auth/resend-verification', {});
      alert('Verification email resent! Please check your inbox.');
      
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Failed to resend verification email');
    } finally {
      setResending(false);
    }
  };

  // ========================================
  // MANUAL REDIRECT (if auto fails)
  // ========================================
  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  // ========================================
  // RENDER LOADING STATE
  // ========================================
  if (status === 'verifying') {
    return (
      <div className="auth-container">
        <div className="auth-box">
          <div className="logo-container">
            <img 
              src="/images/symbols/logo-light.png" 
              alt="Agyapadie Vault" 
              className="logo-image" 
            />
          </div>
          <div className="loading-spinner"></div>
          <h2 className="auth-title">Verifying Your Email</h2>
          <p className="auth-subtitle">Please wait while we verify your email address...</p>
        </div>
      </div>
    );
  }

  // ========================================
  // RENDER SUCCESS STATE
  // ========================================
  if (status === 'success') {
    return (
      <div className="auth-container">
        <div className="auth-box">
          <div className="logo-container">
            <img 
              src="/images/symbols/logo-light.png" 
              alt="Agyapadie Vault" 
              className="logo-image" 
            />
          </div>
          <div className="verification-success-icon">✅</div>
          <h2 className="verification-success-title">Email Verified!</h2>
          <p className="verification-success-message">
            Your email has been successfully verified.
          </p>
          <p className="verification-redirect-message">
            Redirecting you to your dashboard in {countdown} seconds...
          </p>
          <button onClick={handleGoToDashboard} className="verification-manual-btn">
            Go to Dashboard Now →
          </button>
        </div>
      </div>
    );
  }

  // ========================================
  // RENDER ERROR STATE
  // ========================================
  return (
    <div className="auth-container">
      <div className="auth-box">
        <div className="logo-container">
          <img 
            src="/images/symbols/logo-light.png" 
            alt="Agyapadie Vault" 
            className="logo-image" 
          />
        </div>
        <div className="verification-error-icon">⚠️</div>
        <h2 className="verification-error-title">Verification Failed</h2>
        <p className="verification-error-message">{errorMessage}</p>
        
        <div className="verify-prompt-note">
          <p><strong>Common reasons:</strong></p>
          <p>• The verification link has expired (valid for 24 hours)</p>
          <p>• The link has already been used</p>
          <p>• The link was copied incorrectly</p>
        </div>
        
        <button onClick={resendVerification} className="btn-primary" disabled={resending}>
          {resending ? 'Sending...' : 'Resend Verification Email'}
        </button>
        
        <Link to="/login" className="back-link" style={{ marginTop: '15px', display: 'block' }}>
          Back to Login
        </Link>
      </div>
    </div>
  );
};

export default VerifyEmail;